import React, { useEffect, useState } from 'react';
import { api, adminHeaders, formatGBP } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const KINDS = [
    { key: 'sizes', label: 'Sizes' },
    { key: 'frames', label: 'Frames' },
    { key: 'deliveries', label: 'Delivery' },
];

function PriceEditor({ kind, item, onSaved, onClose }) {
    const [data, setData] = useState(item || { name: '', label: '', description: '', price: 0, sort_order: 0, active: true });
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            const payload = {
                name: data.name,
                label: data.label,
                description: data.description || '',
                price: Number(data.price),
                sort_order: Number(data.sort_order) || 0,
                active: !!data.active,
            };
            if (item && item.id) {
                await api.put(`/admin/pricing/${kind}/${item.id}`, payload, { headers: adminHeaders() });
            } else {
                await api.post(`/admin/pricing/${kind}`, payload, { headers: adminHeaders() });
            }
            toast.success('Saved.');
            onSaved();
            onClose();
        } catch (e) {
            toast.error('Save failed.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label>Internal name</Label>
                    <Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} placeholder="e.g. a4" data-testid="pricing-edit-name" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div>
                    <Label>Display label</Label>
                    <Input value={data.label} onChange={(e) => setData({ ...data, label: e.target.value })} placeholder="e.g. A4 — 210 x 297 mm" data-testid="pricing-edit-label" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div className="sm:col-span-2">
                    <Label>Description</Label>
                    <Input value={data.description || ''} onChange={(e) => setData({ ...data, description: e.target.value })} placeholder="Short description" data-testid="pricing-edit-description" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div>
                    <Label>Price (£)</Label>
                    <Input type="number" step="0.01" value={data.price} onChange={(e) => setData({ ...data, price: e.target.value })} data-testid="pricing-edit-price" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div>
                    <Label>Sort order</Label>
                    <Input type="number" value={data.sort_order} onChange={(e) => setData({ ...data, sort_order: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div className="flex items-center gap-2">
                    <Switch checked={!!data.active} onCheckedChange={(v) => setData({ ...data, active: v })} data-testid="pricing-edit-active" />
                    <Label>Active</Label>
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-3">
                <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="pricing-edit-save-button" className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
        </div>
    );
}

function PricingTable({ kind }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    async function load() {
        const r = await api.get(`/admin/pricing/${kind}`, { headers: adminHeaders() });
        setItems(r.data);
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

    async function del(id) {
        if (!window.confirm('Delete this option?')) return;
        await api.delete(`/admin/pricing/${kind}/${id}`, { headers: adminHeaders() });
        toast.success('Deleted.');
        load();
    }

    return (
        <div>
            <div className="flex justify-end mb-3">
                <Dialog open={open && editing === null} onOpenChange={(o) => { if (!o) setEditing(null); setOpen(o); }}>
                    <DialogTrigger asChild>
                        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid={`pricing-add-${kind}`} className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]"><Plus className="h-4 w-4 mr-1" /> Add option</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl bg-[color:var(--paper)] border-[color:var(--border)]">
                        <DialogTitle>Add {kind.slice(0, -1)}</DialogTitle>
                        <PriceEditor kind={kind} item={null} onSaved={load} onClose={() => setOpen(false)} />
                    </DialogContent>
                </Dialog>
            </div>
            <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] overflow-hidden">
                <Table data-testid="admin-pricing-table">
                    <TableHeader>
                        <TableRow className="border-[color:var(--border)]">
                            <TableHead>Label</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Price</TableHead>
                            <TableHead>Active</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((it) => (
                            <TableRow key={it.id} className="border-[color:var(--border)]">
                                <TableCell>{it.label}<div className="text-xs text-[color:var(--muted-fg)]">{it.description}</div></TableCell>
                                <TableCell className="text-xs text-[color:var(--muted-fg)] font-mono">{it.name}</TableCell>
                                <TableCell>{formatGBP(it.price)}</TableCell>
                                <TableCell>{it.active ? 'Yes' : 'No'}</TableCell>
                                <TableCell className="text-right space-x-2">
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`pricing-edit-${it.id}`} className="rounded-xl bg-[color:var(--paper)] border-[color:var(--border)]"><Pencil className="h-3 w-3" /></Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-xl bg-[color:var(--paper)] border-[color:var(--border)]">
                                            <DialogTitle>Edit {it.label}</DialogTitle>
                                            <PriceEditor kind={kind} item={editing && editing.id === it.id ? editing : it} onSaved={load} onClose={() => setEditing(null)} />
                                        </DialogContent>
                                    </Dialog>
                                    <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`pricing-delete-${it.id}`} className="rounded-xl bg-[color:var(--paper)] border-[color:var(--border)]"><Trash2 className="h-3 w-3" /></Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {items.length === 0 && (
                            <TableRow><TableCell colSpan={5} className="text-center py-10 text-[color:var(--muted-fg)]">No options yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default function AdminPricing() {
    const [tab, setTab] = useState('sizes');
    return (
        <div>
            <p className="caption">Pricing</p>
            <h1 className="font-display text-3xl text-[color:var(--umber)] mt-1 mb-6">Sizes, frames & delivery</h1>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-[color:var(--paper-2)] rounded-xl p-1 border border-[color:var(--border)] mb-5">
                    {KINDS.map((k) => (
                        <TabsTrigger key={k.key} value={k.key} data-testid={`pricing-tab-${k.key}`} className="data-[state=active]:bg-[color:var(--paper)] data-[state=active]:text-[color:var(--umber)] rounded-lg px-4">{k.label}</TabsTrigger>
                    ))}
                </TabsList>
                {KINDS.map((k) => (
                    <TabsContent key={k.key} value={k.key}>
                        <PricingTable kind={k.key} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
